import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ReusableService } from 'src/app/services/reusable.service';
import { version as packageVersion } from '../../../../package.json';
import { resolveAppModule } from '../navigation/app-module-resolver';
import { MonitoringApi } from '../http/api/api-monitoring';

/** Endpoint nhận báo lỗi từ FE. Mọi lỗi của CHÍNH nó không bao giờ được báo lại. */
export const FRONTEND_ERROR_REPORT_URL = MonitoringApi.ReportFrontendError;

/** Cùng một khoá (mã trạng thái + đường dẫn) chỉ báo tối đa một lần trong cửa sổ này. */
export const FRONTEND_ERROR_THROTTLE_MS = 30_000;

const MESSAGE_MAX = 2000;
const STACK_MAX = 5000;
const URL_MAX = 2000;
const SHORT_MAX = 500;
/** Trần thân yêu cầu / phản hồi gửi kèm — khớp ReportFrontendErrorValidator.MaxBodyLength. */
export const BODY_MAX = 20_000;

/** Hình dạng thân gửi lên 'monitoring/report-frontend-error' (khớp ReportFrontendErrorRequest). */
export interface IFrontendErrorReport {
  url: string;
  statusCode: number;
  errorId: string | null;
  message: string;
  stackTrace?: string;
  userAgent: string;
  component: string;
  source: 'http' | 'runtime';
  timestamp: string;
  /** E-115 — ảnh chụp của request hỏng; vắng mặt với lỗi runtime. */
  httpMethod?: string;
  requestBody?: string;
  responseBody?: string;
  durationMs?: number;
  appVersion: string;
  /** E-118 — mã module rail của màn đang mở (SM, WM…); vắng khi route không thuộc rail nào. */
  appModule?: string;
  /**
   * E-176 — mã lỗi (khớp ReportFrontendErrorRequest.ErrorCode): CLIENT_TIMEOUT / NETWORK_ERROR khi
   * KHÔNG có phản hồi, hoặc ERR_… đọc được trong thân phản hồi.
   */
  errorCode?: string;
}

/** E-176 — lời gọi HTTP bị `timeoutInterceptor` cắt (rxjs TimeoutError), không có phản hồi nào. */
export const CLIENT_TIMEOUT_CODE = 'CLIENT_TIMEOUT';
/** E-176 — lời gọi HTTP status 0: không tới được máy chủ (mất mạng, CORS, máy chủ tắt). */
export const NETWORK_ERROR_CODE = 'NETWORK_ERROR';

/** Đầu vào của {@link FrontendErrorReporterService.report}. */
export interface IFrontendErrorInput {
  url: string;
  statusCode: number;
  message: string;
  source: 'http' | 'runtime';
  errorId?: string | null;
  stackTrace?: string;
  httpMethod?: string;
  /** Thân đã gửi — bất kỳ hình dạng nào HttpClient nhận; {@link serializeBody} chuẩn hoá. */
  requestBody?: unknown;
  /** Thân máy chủ trả về (raw, trước khi interceptor lỗi dựng lại). */
  responseBody?: unknown;
  durationMs?: number;
  /** E-176 — mã lỗi (xem {@link IFrontendErrorReport.errorCode}). */
  errorCode?: string;
  /**
   * E-176 — route trang LÚC GỬI request. Lỗi hết thời gian tới sau 30 s, khi người dùng có thể đã
   * rời màn; đọc route lúc báo thì lỗi bị gắn nhầm sang màn khác (đo trên dev: lỗi của chi tiết kế
   * hoạch sản xuất ghi PageRoute là /planning/production-request). Vắng ⇒ route hiện tại.
   */
  pageRoute?: string;
}

/**
 * Biến thân request/phản hồi thành chuỗi để gửi kèm báo lỗi.
 *
 * Tệp và FormData không gửi nội dung — chỉ gửi tên trường / kích thước: thứ cần để chẩn đoán là
 * HÌNH DẠNG, còn nội dung tệp vừa nặng vừa có thể nhạy cảm. Trường nhạy cảm trong JSON được máy
 * chủ che lúc ghi (SystemErrorService.RedactSensitiveFields).
 *
 * `undefined` = không có thân; chuỗi rỗng = có phản hồi nhưng thân rỗng — đúng ca 400 do minimal
 * API không đọc được JSON, nên hai trạng thái này phải phân biệt được.
 */
export function serializeBody(body: unknown): string | undefined {
  if (body === null || body === undefined) return undefined;
  let text: string;
  if (typeof body === 'string') {
    text = body;
  } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
    text = `[FormData: ${Array.from(body.keys()).join(', ')}]`;
  } else if (typeof Blob !== 'undefined' && body instanceof Blob) {
    text = `[Blob ${body.type || 'application/octet-stream'}, ${body.size} bytes]`;
  } else if (body instanceof ArrayBuffer) {
    text = `[ArrayBuffer ${body.byteLength} bytes]`;
  } else {
    try {
      text = JSON.stringify(body) ?? '';
    } catch {
      text = '[không tuần tự hoá được]';
    }
  }
  return text.slice(0, BODY_MAX);
}

/** Bỏ query string (và fragment) — token, bộ lọc… không được rời trình duyệt qua kênh này. */
export function stripQuery(url: string): string {
  return (url ?? '').split('#')[0].split('?')[0];
}

/**
 * Gửi báo lỗi FE lên màn Giám sát → Lỗi hệ thống, có chặn bão.
 *
 * Dùng chung cho interceptor HTTP và ErrorHandler toàn cục. Phụ thuộc (Router,
 * ReusableService) lấy LƯỜI qua Injector: ErrorHandler được dựng rất sớm, và
 * ReusableService → HttpClient → interceptor → service này là một vòng nếu tiêm thẳng.
 *
 * Không bao giờ ném: mọi lỗi của chính đường báo cáo bị nuốt, vì nếu nó ném thì
 * ErrorHandler sẽ nhận lỗi đó và tự báo lại chính mình.
 */
@Injectable({ providedIn: 'root' })
export class FrontendErrorReporterService {
  private readonly injector = inject(Injector);
  /** khoá → thời điểm báo gần nhất (ms). */
  private readonly lastReported = new Map<string, number>();

  /** Cho spec: xoá bộ nhớ chặn bão. */
  resetThrottle(): void {
    this.lastReported.clear();
  }

  /** Lỗi đến từ chính endpoint báo cáo ⇒ không bao giờ báo. */
  isReportUrl(url: string | null | undefined): boolean {
    return !!url && url.includes(FRONTEND_ERROR_REPORT_URL);
  }

  /** Đường dẫn route hiện tại; '' nếu Router chưa sẵn sàng. */
  currentRoute(): string {
    try {
      return this.injector.get(Router, null)?.url ?? '';
    } catch {
      return '';
    }
  }

  /**
   * Báo một lỗi. Trả về true nếu đã gửi, false nếu bị bỏ (url báo cáo, hoặc trùng khoá
   * trong cửa sổ chặn bão).
   */
  report(input: IFrontendErrorInput, now: number = Date.now()): boolean {
    try {
      const path = stripQuery(input.url);
      if (!path || this.isReportUrl(path)) {
        return false;
      }

      const key = `${input.source}|${input.statusCode}|${path}|${
        input.source === 'runtime' ? (input.message ?? '').slice(0, 200) : ''
      }`;
      const last = this.lastReported.get(key);
      if (last !== undefined && now - last < FRONTEND_ERROR_THROTTLE_MS) {
        return false;
      }
      this.lastReported.set(key, now);
      this.pruneThrottle(now);

      const route = input.pageRoute || this.currentRoute();
      const report: IFrontendErrorReport = {
        url: path.slice(0, URL_MAX),
        statusCode: input.statusCode,
        errorId: input.errorId ? String(input.errorId).slice(0, 100) : null,
        message: (input.message ?? '').slice(0, MESSAGE_MAX),
        userAgent: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(
          0,
          SHORT_MAX,
        ),
        component: stripQuery(route).slice(0, SHORT_MAX),
        source: input.source,
        timestamp: new Date(now).toISOString(),
        appVersion: String(packageVersion ?? '').slice(0, 64),
      };
      const appModule = resolveAppModule(route);
      if (appModule) {
        report.appModule = appModule.id;
      }
      if (input.errorCode) {
        report.errorCode = input.errorCode.slice(0, 64);
      }
      if (input.stackTrace) {
        report.stackTrace = input.stackTrace.slice(0, STACK_MAX);
      }
      if (input.httpMethod) {
        report.httpMethod = input.httpMethod.slice(0, 10);
      }
      const requestBody = serializeBody(input.requestBody);
      if (requestBody !== undefined) {
        report.requestBody = requestBody;
      }
      const responseBody = serializeBody(input.responseBody);
      if (responseBody !== undefined) {
        report.responseBody = responseBody;
      }
      if (typeof input.durationMs === 'number' && input.durationMs >= 0) {
        report.durationMs = Math.round(input.durationMs);
      }

      const reusable = this.injector.get(ReusableService);
      // Fire-and-forget; update=false, showToast=false. Lỗi của đường báo cáo bị nuốt.
      reusable.post(FRONTEND_ERROR_REPORT_URL, report, false, false).subscribe({
        error: () => {
          /* nuốt im lặng — xem doc-comment lớp */
        },
      });
      return true;
    } catch {
      return false;
    }
  }

  /** Giữ Map không phình vô hạn trong phiên dài: bỏ các khoá đã hết cửa sổ. */
  private pruneThrottle(now: number): void {
    if (this.lastReported.size < 200) return;
    for (const [k, t] of this.lastReported) {
      if (now - t >= FRONTEND_ERROR_THROTTLE_MS) {
        this.lastReported.delete(k);
      }
    }
  }
}
